import { PieSeriesType } from '@mui/x-charts'

export const transformTeamDataToPieChart = (
  teamData: Record<string, { total: number; registered: number; unregistered: number }>,
  options: {
    valueType?: 'total' | 'registered' | 'unregistered'
    colorPalette?: string[]
    sortBy?: 'value' | 'label' | 'none'
    sortOrder?: 'asc' | 'desc'
    labelFormatter?: (label: string, value: number) => string
    maxEntries?: number
    minValueThreshold?: number
    othersLabel?: string
  } = {},
): PieSeriesType['data'] => {
  const {
    valueType = 'total',
    colorPalette = [
      '#4B0082',
      '#FF6347',
      '#32CD32',
      '#1E90FF',
      '#FF4500',
      '#00CED1',
      '#8A2BE2',
      '#20B2AA',
    ],
    sortBy = 'value',
    sortOrder = 'desc',
    labelFormatter = (label) => label,
    maxEntries = Infinity,
    minValueThreshold = 0,
    othersLabel = 'Others',
  } = options

  let processedData = Object.entries(teamData)
    .map(([label, stats], index) => ({
      id: index,
      value: stats[valueType],
      label: labelFormatter(label, stats[valueType]),
      color: colorPalette[index % colorPalette.length],
    }))
    .filter((item) => item.value >= minValueThreshold)

  // Sort the data
  if (sortBy !== 'none') {
    processedData.sort((a, b) => {
      const comparison = sortBy === 'value' ? b.value - a.value : a.label.localeCompare(b.label)
      return sortOrder === 'desc' ? comparison : -comparison
    })
  }

  // Limit entries and handle "Others"
  if (processedData.length > maxEntries) {
    const mainEntries = processedData.slice(0, maxEntries - 1)
    const othersValue = processedData.slice(maxEntries - 1).reduce((sum, item) => sum + item.value, 0)

    processedData = [
      ...mainEntries,
      {
        id: processedData.length,
        value: othersValue,
        label: othersLabel,
        color: colorPalette[mainEntries.length % colorPalette.length],
      },
    ]
  }

  return processedData
}

export const transformTeamDataToBarChart = (
  teamData: Record<string, { total: number; registered: number; unregistered: number }> = {},
  options: {
    series?: ('total' | 'registered' | 'unregistered')[]
    sortBy?: 'value' | 'label' | 'none'
    sortOrder?: 'asc' | 'desc'
    labelFormatter?: (label: string) => string
    maxEntries?: number
    minValueThreshold?: number
  } = {},
) => {
  const {
    series = ['total', 'registered', 'unregistered'],
    sortBy = 'value',
    sortOrder = 'desc',
    labelFormatter = (label) => label,
    maxEntries = Infinity,
    minValueThreshold = 0,
  } = options

  type TeamDataItem = {
    label: string
    total: number
    registered: number
    unregistered: number
  }

  // If teamData is empty, return an empty array
  if (Object.keys(teamData).length === 0) {
    return []
  }

  let processedData: TeamDataItem[] = Object.entries(teamData)
    .map(([label, stats]) => {
      // Ensure stats has the required properties with default values
      const safeStats = {
        total: stats.total || 0,
        registered: stats.registered || 0,
        unregistered: stats.unregistered || 0,
      }

      const seriesData = series.reduce(
        (acc, seriesKey) => {
          acc[seriesKey] = safeStats[seriesKey]
          return acc
        },
        {} as Pick<TeamDataItem, 'total' | 'registered' | 'unregistered'>,
      )

      return {
        label: labelFormatter(label),
        ...seriesData,
      } as TeamDataItem
    })
    .filter((item) => series.some((seriesKey) => item[seriesKey] >= minValueThreshold))

  // Sort the data
  if (sortBy !== 'none') {
    processedData.sort((a, b) => {
      const comparison =
        sortBy === 'value'
          ? Number(b[series[0] as keyof TeamDataItem] || 0) -
            Number(a[series[0] as keyof TeamDataItem] || 0)
          : a.label.localeCompare(b.label)
      return sortOrder === 'desc' ? comparison : -comparison
    })
  }

  // Limit entries
  if (processedData.length > maxEntries) {
    processedData = processedData.slice(0, maxEntries)
  }

  return processedData
}

export const transformTeamTaskDataToBarChart = (
  teamData: Record<
    string,
    {
      total: number
      inProgress?: number
      notStarted?: number
      completed?: number
      overallComplianceRate?: number
      onTimeDeliveryRate?: number
      overdueRate?: number
    }
  > = {},
  options: {
    series?: (
      | 'total'
      | 'inProgress'
      | 'notStarted'
      | 'completed'
      | 'overallComplianceRate'
      | 'onTimeDeliveryRate'
      | 'overdueRate'
    )[]
    sortBy?: 'value' | 'label' | 'none'
    sortOrder?: 'asc' | 'desc'
    labelFormatter?: (label: string) => string
    maxEntries?: number
    minValueThreshold?: number
  } = {},
) => {
  const {
    series = ['overallComplianceRate', 'onTimeDeliveryRate', 'overdueRate'],
    sortBy = 'value',
    sortOrder = 'desc',
    labelFormatter = (label) => label,
    maxEntries = Infinity,
    minValueThreshold = 0,
  } = options

  type TeamTaskDataItem = {
    label: string
    total: number
    inProgress: number
    notStarted: number
    completed: number
    overallComplianceRate: number
    onTimeDeliveryRate: number
    overdueRate: number
  }

  // If teamData is empty, return an empty array
  if (Object.keys(teamData).length === 0) {
    return []
  }

  let processedData: TeamTaskDataItem[] = Object.entries(teamData)
    .map(([label, stats]) => {
      // Ensure stats has the required properties with default values
      const safeStats: TeamTaskDataItem = {
        label: labelFormatter(label),
        total: stats.total || 0,
        inProgress: stats.inProgress || 0,
        notStarted: stats.notStarted || 0,
        completed: stats.completed || 0,
        overallComplianceRate: stats.overallComplianceRate || 0,
        onTimeDeliveryRate: stats.onTimeDeliveryRate || 0,
        overdueRate: stats.overdueRate || 0,
      }

      const seriesData = series.reduce(
        (acc, seriesKey) => {
          acc[seriesKey] = safeStats[seriesKey]
          return acc
        },
        {} as Partial<TeamTaskDataItem>,
      )

      return {
        label: labelFormatter(label),
        ...seriesData,
      } as TeamTaskDataItem
    })
    .filter((item) => series.some((seriesKey) => (item[seriesKey] as number) >= minValueThreshold))

  // Sort the data
  if (sortBy !== 'none') {
    processedData.sort((a, b) => {
      const comparison =
        sortBy === 'value'
          ? Number(b[series[0] as keyof TeamTaskDataItem] || 0) -
            Number(a[series[0] as keyof TeamTaskDataItem] || 0)
          : a.label.localeCompare(b.label)
      return sortOrder === 'desc' ? comparison : -comparison
    })
  }

  // Limit entries
  if (processedData.length > maxEntries) {
    processedData = processedData.slice(0, maxEntries)
  }

  return processedData
}
